import { createSlice } from '@reduxjs/toolkit'
import { createAxiosThunk } from 'utils/reduxUtil'
import api from 'api/axios/foka'
import {
  setAssetChecked,
  setChannel,
  setCreativeName,
} from 'features/creative/add/build/buildCreativeSlice'
import {
  setBasicCreativeBothLegalNoticeForm,
  setBasicCreativeForm,
} from 'features/creative/add/basic/basicCreativeSlice'
import { setThumbnail } from 'features/creative/add/asset/thumbnailSlice'
import {
  setAllIndividual as setAllMultiThumbnailIndividual,
  setMultiThumbnail,
} from 'features/creative/add/asset/multiThumbnailSlice'
import { setAdditionalTitle } from 'features/creative/add/asset/additionalTitleSlice'
import {
  setAdditionalLink,
  setAllIndividualChecked as setAllAdditionalLinkIndividual,
} from 'features/creative/add/asset/additionalLinkSlice'
import {
  setIndividual as setSubjectIndividual,
  setSubject,
} from 'features/creative/add/asset/subjectSlice'
import {
  setAllIndividualChecked as setAllPriceTableIndividualChecked,
  setPriceTable,
} from 'features/creative/add/asset/priceTableSlice'
import {
  setCalculate,
  setIndividualChecked as setCalculateIndividualChecked,
} from 'features/creative/add/asset/calculateSlice'
import { setPhoneNumber } from 'features/creative/add/asset/phoneNumberSlice'
import { setTalkChannel } from 'features/creative/add/asset/talkChannelSlice'
import {
  convertTalkChannelAssets,
  fillCreativeEmptyLandingUrl,
  getImageColors,
} from 'features/creative/creativeUtil'
import { batch } from 'react-redux'
import { BizChannelTypes } from '../../../Codes'
import { importThumbnailCustom } from '../asset/thumbnailCustomSlice'
import { importThumbnailRecommend } from '../asset/thumbnailRecommendSlice'

const initialState = {
  selectedCreative: null,
  creativeDetail: {
    title: '제목',
    description: '설명문구',
    landingUrl: {
      individualSettingYn: 'Y',
    },
    assets: [],
  },
  searchText: '',
  searchResult: [],
  apiError: null,
  loading: false,
}

export const importCreative = async ({ creative, channel, dispatch }) => {
  const { bizChannelType } = channel
  batch(() => {
    dispatch(setChannel(channel))
    dispatch(setCreativeName(creative.name))
  })
  if (bizChannelType === BizChannelTypes.TALK_CHANNEL) {
    const { thumbnailRecommend, thumbnailCustom } = convertTalkChannelAssets(
      creative.assets
    )
    thumbnailRecommend.dominantColors = await getImageColors(
      thumbnailRecommend.thumbnail.url
    )
    thumbnailCustom.dominantColors = await getImageColors(
      thumbnailCustom.thumbnail.url
    )
    batch(() => {
      if (
        creative.licenseReviewYn === 'Y' &&
        creative.license?.customLicenseNumber &&
        creative.license?.recommendLicenseNumber
      ) {
        dispatch(
          setBasicCreativeBothLegalNoticeForm({
            licenseReview: creative.licenseReviewYn === 'Y',
            license: {
              customLicenseNumber: creative.license.customLicenseNumber,
              customLicenseDocument: channel?.reviewDocuments.find(
                doc => doc.id === creative.license.customLicenseDocumentId
              ),
              recommendLicenseNumber: creative.license.recommendLicenseNumber,
              recommendLicenseDocument: channel?.reviewDocuments.find(
                doc => doc.id === creative.license.recommendLicenseDocumentId
              ),
            },
          })
        )
      } else if (creative.licenseReviewYn === 'Y') {
        dispatch(
          setBasicCreativeForm({
            licenseReview: creative.licenseReviewYn === 'Y',
            licenseNumber: creative.licenseNumber,
            licenseDocument: channel?.reviewDocuments.find(
              doc => doc.id === creative.licenseDocumentId
            ),
          })
        )
      }
      dispatch(importThumbnailRecommend(thumbnailRecommend))
      dispatch(importThumbnailCustom(thumbnailCustom))
    })
  } else {
    const filledCreative = fillCreativeEmptyLandingUrl(
      creative,
      channel.siteUrl
    )
    dispatch(
      setBasicCreativeForm({
        title: filledCreative.title || '',
        description: filledCreative.description || '',
        landingUrl: filledCreative.landingUrl,
        ...(filledCreative.licenseReviewYn === 'Y' && {
          licenseReview: filledCreative.licenseReviewYn === 'Y',
          licenseNumber: filledCreative.licenseNumber,
          licenseDocument: filledCreative.licenseDocumentDeleted
            ? {
                id: filledCreative.licenseDocumentId,
                filename: filledCreative.licenseDocumentName,
                createdDate: filledCreative.licenseDocumentCreatedDate,
                deleted: true,
              }
            : channel?.reviewDocuments.find(
                doc => doc.id === filledCreative.licenseDocumentId
              ),
        }),
      })
    )
    const individual = channel?.siteUrl?.individualSettingYn === 'Y'
    batch(() => {
      dispatch(setAllMultiThumbnailIndividual(individual))
      dispatch(setAllAdditionalLinkIndividual(individual))
      dispatch(setSubjectIndividual(individual))
      dispatch(setAllPriceTableIndividualChecked(individual))
      dispatch(setCalculateIndividualChecked(individual))
    })
    const activeAssets = {}
    filledCreative.assets.forEach(({ type: { name }, data }) => {
      switch (name) {
        case 'THUMBNAIL':
          activeAssets.thumbnail = true
          dispatch(setThumbnail(data))
          break
        case 'MULTI_THUMBNAIL':
          activeAssets.multiThumbnail = true
          dispatch(setMultiThumbnail(data))
          break
        case 'ADDITIONAL_TITLE':
          activeAssets.additionalTitle = true
          dispatch(setAdditionalTitle(data))
          break
        case 'ADDITIONAL_LINK':
          activeAssets.additionalLink = true
          dispatch(setAdditionalLink(data))
          break
        case 'SUBJECT':
          activeAssets.subject = true
          dispatch(setSubject(data))
          break
        case 'PRICE_TABLE':
          activeAssets.priceTable = true
          dispatch(setPriceTable(data))
          break
        case 'CALCULATE':
          activeAssets.calculate = true
          dispatch(setCalculate(data))
          break
        case 'PHONE_NUMBER':
          activeAssets.phoneNumber = true
          dispatch(setPhoneNumber(data))
          break
        case 'TALK_CHANNEL':
          activeAssets.talkChannel = true
          dispatch(setTalkChannel(data))
          break
        default:
      }
    })
    dispatch(setAssetChecked(activeAssets))
  }
}

export const fetchCreative = createAxiosThunk(
  'creativeImport/fetchCreative',
  (noArgs, { getState }) => {
    const { selectedCreative } = getState().creative.creativeImport
    return api.creative.getCreative(selectedCreative.id)
  }
)

const creativeImportSlice = createSlice({
  name: 'creativeImport',
  initialState,

  reducers: {
    reset: () => initialState,
    setSelectedCreative: (state, { payload: selectedCreative }) => {
      state.selectedCreative = selectedCreative
    },
    setSearchText: (state, { payload: searchText }) => {
      state.searchText = searchText
    },
    setSearchResult: (state, { payload: searchResult }) => {
      state.searchResult = searchResult
    },
    resetCreativeDetail: state => {
      state.creativeDetail = initialState.creativeDetail
    },
  },

  extraReducers: builder => {
    builder.addCase(fetchCreative.pending, state => {
      state.loading = true
    })

    builder.addCase(fetchCreative.fulfilled, (state, { payload }) => {
      state.creativeDetail = payload.creative
      state.loading = false
    })

    builder.addCase(fetchCreative.rejected, (state, { payload }) => {
      state.apiError = payload || true
      state.loading = false
    })
  },
})

export const {
  reset,
  resetCreativeDetail,
  setSearchResult,
  setSearchText,
  setSelectedCreative,
} = creativeImportSlice.actions

export default creativeImportSlice.reducer
